import { useCallback, useEffect, useMemo, useState } from 'react'
import { ActionIcon, Badge, Button, Card, Group, NumberInput, Select, Stack, Text, TextInput } from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { IconCopy, IconExternalLink, IconRefresh } from '@/components/icons'
import {
  DataTable,
  DataTableScroll,
  DataTbody,
  DataTd,
  DataTh,
  DataThead,
  DataTr,
  EmptyState,
  PageCenterLoader,
  PaginationBar,
} from '@/components/ui'
import { api, ApiError } from '@/lib/api'
import { debugDatePreset } from '@/lib/debug-date-presets'
import { formatDateTimeDot, statusLabel, ticketPathSegmentFromListItem } from '@/lib/ticket-display'
import type { DebugSlowFirstResponseRow, ProjectRow, WorkspaceUserRow } from '@/types/api'

const PAGE_SIZE_LS = 'sd2_debug_slow_first_response_page_size'
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const

type Props = {
  projects: ProjectRow[]
  users: WorkspaceUserRow[]
  openTicket: (ticket: { id: string; ticket_number: number; project_slug?: string | null }) => void
}

function readPageSize(): number {
  try {
    const n = parseInt(localStorage.getItem(PAGE_SIZE_LS) || '', 10)
    if (PAGE_SIZE_OPTIONS.includes(n as (typeof PAGE_SIZE_OPTIONS)[number])) return n
  } catch {
    /* ignore */
  }
  return 50
}

function userLabel(user: WorkspaceUserRow): string {
  const name = [user.first_name, user.last_name].filter(Boolean).join(' ').trim()
  return name || user.username || user.email || user.id
}

function isHumanOperator(user: WorkspaceUserRow): boolean {
  const role = String(user.role || '').toLowerCase()
  const email = String(user.email || '').toLowerCase()
  return (
    user.is_active !== false &&
    user.is_deleted !== true &&
    !email.includes('ai-assistant') &&
    ['operator', 'trainee', 'shift_lead', 'npc_helper', 'human_helper', 'analyst'].includes(role)
  )
}

function formatResponseTime(seconds: number): string {
  const total = Math.max(0, Math.round(seconds || 0))
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const secs = total % 60
  if (hours > 0) return `${hours} ч ${minutes} мин`
  if (minutes > 0) return `${minutes} мин ${secs} с`
  return `${secs} с`
}

function ticketHref(row: DebugSlowFirstResponseRow): string {
  const ref = ticketPathSegmentFromListItem(row)
  return `${window.location.origin}/tickets/${encodeURIComponent(ref)}`
}

export function DebugSlowFirstResponseTab({ projects, users, openTicket }: Props) {
  const [rows, setRows] = useState<DebugSlowFirstResponseRow[]>([])
  const [total, setTotal] = useState(0)
  const [pages, setPages] = useState(1)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [refreshKey, setRefreshKey] = useState(0)

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(readPageSize)
  const [hours, setHours] = useState(24)
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [projectId, setProjectId] = useState('')
  const [operatorId, setOperatorId] = useState('')
  const [sortBy, setSortBy] = useState('first_response_minutes')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')

  const projectSelectData = useMemo(
    () => [
      { value: '', label: 'Все проекты' },
      ...projects.map((project) => ({
        value: project.id,
        label: project.name || project.slug || project.id,
      })),
    ],
    [projects],
  )

  const operatorSelectData = useMemo(
    () => [
      { value: '', label: 'Все операторы' },
      ...users
        .filter(isHumanOperator)
        .sort((a, b) => userLabel(a).localeCompare(userLabel(b), 'ru'))
        .map((user) => ({ value: user.id, label: userLabel(user) })),
    ],
    [users],
  )

  const resetPage = useCallback(() => setPage(1), [])

  useEffect(() => {
    localStorage.setItem(PAGE_SIZE_LS, String(pageSize))
  }, [pageSize])

  useEffect(() => {
    let alive = true
    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const params: Record<string, string | number | undefined> = {
          project_id: projectId || undefined,
          operator_id: operatorId || undefined,
          page,
          page_size: pageSize,
          sort_by: sortBy,
          sort_dir: sortDir,
          threshold_minutes: 10,
        }
        if (dateFrom && dateTo) {
          params.date_from = dateFrom
          params.date_to = dateTo
        } else {
          params.updated_within_hours = hours
        }
        const res = await api.debugSlowFirstResponse(params)
        if (!alive) return
        setRows(res.items ?? [])
        setTotal(typeof res.total === 'number' ? res.total : 0)
        setPages(typeof res.pages === 'number' && res.pages > 0 ? res.pages : 1)
      } catch (e) {
        if (!alive) return
        setRows([])
        setTotal(0)
        setPages(1)
        setError(e instanceof ApiError ? e.message : 'Не удалось загрузить обращения')
      } finally {
        if (alive) setLoading(false)
      }
    }
    void load()
    return () => {
      alive = false
    }
  }, [dateFrom, dateTo, hours, operatorId, page, pageSize, projectId, refreshKey, sortBy, sortDir])

  const copyLink = useCallback(async (row: DebugSlowFirstResponseRow) => {
    try {
      await navigator.clipboard.writeText(ticketHref(row))
      showNotification({ title: 'Ссылка скопирована', message: `Обращение #${row.ticket_number}`, color: 'green' })
    } catch {
      showNotification({ title: 'Не удалось скопировать', message: 'Браузер не дал доступ к clipboard', color: 'red' })
    }
  }, [])

  return (
    <Stack gap="sm">
      <Card withBorder padding="sm" radius="md">
        <Stack gap="sm">
          <Group align="flex-end" wrap="wrap" gap="sm">
            <NumberInput
              size="xs"
              label="Часов"
              value={hours}
              onChange={(value) => {
                setHours(typeof value === 'number' ? value : 24)
                setDateFrom('')
                setDateTo('')
                resetPage()
              }}
              min={1}
              max={8760}
              w={90}
            />
            <TextInput
              size="xs"
              type="date"
              label="С даты"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.currentTarget.value)
                resetPage()
              }}
              w={150}
            />
            <TextInput
              size="xs"
              type="date"
              label="По дату"
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.currentTarget.value)
                resetPage()
              }}
              w={150}
            />
            <Group gap={4}>
              <Button
                size="compact-xs"
                variant="light"
                onClick={() => {
                  const { from, to } = debugDatePreset('yesterday')
                  setDateFrom(from)
                  setDateTo(to)
                  resetPage()
                }}
              >
                Вчера
              </Button>
              <Button
                size="compact-xs"
                variant="light"
                onClick={() => {
                  const { from, to } = debugDatePreset('last7')
                  setDateFrom(from)
                  setDateTo(to)
                  resetPage()
                }}
              >
                7 дн.
              </Button>
              <Button
                size="compact-xs"
                variant="light"
                onClick={() => {
                  const { from, to } = debugDatePreset('last_week')
                  setDateFrom(from)
                  setDateTo(to)
                  resetPage()
                }}
              >
                Пн-Вс
              </Button>
            </Group>
            <Select
              size="xs"
              label="Проект"
              data={projectSelectData}
              value={projectId}
              onChange={(value) => {
                setProjectId(value || '')
                resetPage()
              }}
              searchable
              allowDeselect={false}
              checkIconPosition="right"
              styles={{ root: { width: 220 } }}
            />
            <Select
              size="xs"
              label="Оператор"
              data={operatorSelectData}
              value={operatorId}
              onChange={(value) => {
                setOperatorId(value || '')
                resetPage()
              }}
              searchable
              allowDeselect={false}
              checkIconPosition="right"
              styles={{ root: { width: 220 } }}
            />
            <Select
              size="xs"
              label="Сортировка"
              data={[
                { value: 'first_response_minutes', label: 'Время ответа' },
                { value: 'project', label: 'Проект' },
                { value: 'ticket_number', label: 'Номер тикета' },
                { value: 'created_at', label: 'Дата создания' },
                { value: 'updated_at', label: 'Дата обновления' },
                { value: 'operator', label: 'Оператор' },
                { value: 'first_response_at', label: 'Дата первого ответа' },
              ]}
              value={sortBy}
              onChange={(value) => {
                setSortBy(value || 'first_response_minutes')
                resetPage()
              }}
              allowDeselect={false}
              checkIconPosition="right"
              styles={{ root: { width: 190 } }}
            />
            <Select
              size="xs"
              label="Порядок"
              data={[
                { value: 'desc', label: 'По убыванию' },
                { value: 'asc', label: 'По возрастанию' },
              ]}
              value={sortDir}
              onChange={(value) => {
                setSortDir(value === 'asc' ? 'asc' : 'desc')
                resetPage()
              }}
              allowDeselect={false}
              checkIconPosition="right"
              styles={{ root: { width: 150 } }}
            />
            <Select
              size="xs"
              label="На странице"
              data={PAGE_SIZE_OPTIONS.map((value) => ({ value: String(value), label: String(value) }))}
              value={String(pageSize)}
              onChange={(value) => {
                setPageSize(Number(value || 50))
                resetPage()
              }}
              allowDeselect={false}
              checkIconPosition="right"
              w={110}
            />
            <ActionIcon variant="default" size="md" aria-label="Обновить" onClick={() => setRefreshKey((value) => value + 1)}>
              <IconRefresh size={16} stroke={1.5} />
            </ActionIcon>
          </Group>
          <Text size="xs" c="dimmed">
            Показываются обращения, где первый публичный ответ активного оператора после назначения или перераспределения занял больше 10 минут. Мозгобот не учитывается.
          </Text>
        </Stack>
      </Card>

      <Card withBorder padding="sm" radius="md">
        <Group justify="space-between" mb="sm" wrap="wrap">
          <Text size="xs" c="dimmed">
            Найдено: <strong>{total}</strong>
          </Text>
          {pages > 1 ? <PaginationBar page={page} pageCount={pages} total={total} disabled={loading} onPageChange={setPage} /> : null}
        </Group>

        {error ? (
          <EmptyState title="Ошибка" description={error} />
        ) : loading && rows.length === 0 ? (
          <PageCenterLoader label="Загрузка" minHeight={180} />
        ) : rows.length ? (
          <DataTableScroll>
            <DataTable>
              <DataThead>
                <DataTr>
                  <DataTh>Проект</DataTh>
                  <DataTh>Тикет</DataTh>
                  <DataTh>Создано</DataTh>
                  <DataTh>Обновлено</DataTh>
                  <DataTh>Первый ответил</DataTh>
                  <DataTh>До ответа</DataTh>
                  <DataTh>Текст первого ответа</DataTh>
                  <DataTh>Действия</DataTh>
                </DataTr>
              </DataThead>
              <DataTbody>
                {rows.map((row) => (
                  <DataTr key={row.segment_key || row.id}>
                    <DataTd clip>{row.project_name || row.project_id || '—'}</DataTd>
                    <DataTd>
                      <Text size="xs" fw={600}>
                        #{row.ticket_number}
                      </Text>
                      <Text size="xs" c="dimmed" lineClamp={2}>
                        {row.subject || statusLabel(row.status || '')}
                      </Text>
                    </DataTd>
                    <DataTd muted>
                      <Text size="xs">{row.created_at ? formatDateTimeDot(row.created_at) : '—'}</Text>
                    </DataTd>
                    <DataTd muted>
                      <Text size="xs">{row.updated_at ? formatDateTimeDot(row.updated_at) : '—'}</Text>
                    </DataTd>
                    <DataTd>
                      <Text size="xs" fw={600} lineClamp={1}>
                        {row.operator_name || row.operator_email || row.operator_id}
                      </Text>
                      <Text size="xs" c="dimmed" lineClamp={1}>
                        {row.first_response_at ? formatDateTimeDot(row.first_response_at) : '—'}
                      </Text>
                    </DataTd>
                    <DataTd>
                      <Badge color="red" variant="light">
                        {formatResponseTime(row.first_response_seconds)}
                      </Badge>
                    </DataTd>
                    <DataTd>
                      <Text size="xs" lineClamp={3} style={{ maxWidth: 420, whiteSpace: 'pre-wrap' }}>
                        {row.first_response_text || '—'}
                      </Text>
                    </DataTd>
                    <DataTd>
                      <Group gap={4} wrap="nowrap">
                        <ActionIcon
                          size="sm"
                          variant="light"
                          title="Открыть обращение"
                          onClick={() => openTicket(row)}
                        >
                          <IconExternalLink size={14} />
                        </ActionIcon>
                        <ActionIcon size="sm" variant="light" title="Скопировать ссылку" onClick={() => void copyLink(row)}>
                          <IconCopy size={14} />
                        </ActionIcon>
                      </Group>
                    </DataTd>
                  </DataTr>
                ))}
              </DataTbody>
            </DataTable>
          </DataTableScroll>
        ) : (
          <EmptyState title="Нет обращений" description="За выбранный период задержек первого ответа больше 10 минут не найдено." />
        )}
      </Card>
    </Stack>
  )
}
